import React, { SetStateAction } from 'react';
import {
  MobileRow,
  MobileRowContent,
  MobileRowImage,
  MobileRowMeta,
  MobileRowHeader
} from 'components/Lists/MobileList';
import { TombstoneText } from 'components/Tombstone';
import { Badge } from '@protonradio/proton-ui';
import {
  CheckmarkCircleIcon,
  EnvelopeIcon,
  SpotifyIcon,
  StarIcon
} from 'components/Icons';
import { styled } from 'styled-components';
import { COLORS } from 'config/constants';
import Flex from 'components/Flex/Flex';

import { formatDuration } from 'helpers';
import { getArchiveAccessExpirationFormatted } from './helpers';
import Star from 'components/Icons/Star';
import { LabelSubscription } from './LabelSubscribersContext';
import { SortByConfig } from 'components/TableFilterControls/TableFilterControls';

function isOdd(num: number) {
  return num % 2 !== 0;
}

const AcceptedIconSize = 18;
const AcceptedIconWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  height: ${AcceptedIconSize + 1}px;
  width: ${AcceptedIconSize + 1}px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${COLORS.WHITE};
`;

const SortContainer = styled.div`
  align-items: center;
  height: 50px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  color: ${COLORS.GRAY_MEDIUM_LIGHT};
`;

// We need to tighten things up to fit the sort icons
const ArchiveAccessBadge = styled.div`
  div {
    font-size: 10px;
  }
`;

const DateInvitedSortIcon = ({ subscription }: { subscription: LabelSubscription }) => {
  if (!subscription?.node) return null;
  const formattedDate = formatDuration(subscription.addedAt);

  // NOTE: We absolutely postion this to prevent wrapping when the archive access badge is present
  return (
    <div style={{ position: 'absolute', right: 3, top: 27 }}>
      <Flex align="center">
        <EnvelopeIcon size={14} color="currentColor" />
        <div style={{ marginLeft: 3, fontWeight: 500 }}>{formattedDate} ago</div>
      </Flex>
    </div>
  );
};

const SORT_ICONS = {
  averageRating: ({ subscription }: { subscription: LabelSubscription }) => {
    if (!subscription?.node) return null;
    // round to nearest 0.1
    const rating = (Math.round(subscription.averageRating * 10) / 10).toFixed(1);

    return (
      <Flex align="center" grow={0}>
        <StarIcon color="currentColor" size={14} />
        <div style={{ marginLeft: 3 }}>{rating}</div>
      </Flex>
    );
  },
  artistPopularity: ({ subscription }: { subscription: LabelSubscription }) => {
    if (!subscription?.node) return null;

    return (
      <Flex align="center">
        <SpotifyIcon size={16} color="currentColor" />
        <div style={{ marginLeft: 3, fontWeight: 500 }}>
          {subscription.node.spotifyPopularity}
        </div>
      </Flex>
    );
  },
  dateInvited: DateInvitedSortIcon,
  artistName: DateInvitedSortIcon,
  lastReactedAt: ({ subscription }: { subscription: LabelSubscription }) => {
    if (!subscription?.node) return null;
    const formattedDate = formatDuration(subscription.lastReactedAt);

    // NOTE: We absolutely postion this to prevent wrapping when the archive access badge is present
    return (
      <div style={{ position: 'absolute', right: 3, top: 27 }}>
        <Flex align="center">
          <Star size={14} color="currentColor" />
          <div style={{ marginLeft: 3, fontWeight: 500 }}>{formattedDate} ago</div>
        </Flex>
      </div>
    );
  }
};

const LabelSubscribersRowMobile = ({
  subscription,
  setSelectedSubscriber,
  sortBy
}: {
  subscription: LabelSubscription;
  setSelectedSubscriber: React.Dispatch<SetStateAction<LabelSubscription>>;
  sortBy: SortByConfig<keyof typeof SORT_ICONS>;
}) => {
  const {
    id,
    node: artist,
    hasArchiveAccess,
    archiveAccessExpiresAt,
    isFollowing
  } = subscription;

  const archiveExpirationFormatted = archiveAccessExpiresAt
    ? getArchiveAccessExpirationFormatted(archiveAccessExpiresAt)
    : null;

  const SortIcon = SORT_ICONS[Object.keys(sortBy)[0] as keyof typeof SORT_ICONS];

  return (
    <MobileRow
      key={id}
      data-testid="label-subscribers-row"
      onClick={() => {
        setSelectedSubscriber(subscription);
      }}
      // disabled={disabled}
      role="button"
      tabIndex={0}
    >
      <MobileRowContent
        data-testid={LabelSubscribersRowMobile.TEST_IDS.rowId(artist?.id)}
      >
        <div style={{ position: 'relative' }}>
          <MobileRowImage imageUrl={artist?.image?.url} type="artist" />

          {isFollowing && (
            <AcceptedIconWrapper>
              <CheckmarkCircleIcon
                title="Accepted"
                size={AcceptedIconSize}
                color={COLORS.SUCCESS}
              />
            </AcceptedIconWrapper>
          )}
        </div>

        <MobileRowMeta>
          <MobileRowHeader>{artist!.name}</MobileRowHeader>

          {hasArchiveAccess && (
            <div data-testid={LabelSubscribersRowMobile.TEST_IDS.archvieAccess}>
              <ArchiveAccessBadge>
                <Badge>Archive Access • {archiveExpirationFormatted}</Badge>
              </ArchiveAccessBadge>
            </div>
          )}
        </MobileRowMeta>
        {SortIcon && (
          <SortContainer>
            <SortIcon subscription={subscription} />
          </SortContainer>
        )}
      </MobileRowContent>
    </MobileRow>
  );
};

LabelSubscribersRowMobile.Tombstone = ({ index }: { index: number }) => (
  <MobileRow key={index}>
    <MobileRowContent>
      <MobileRowImage type="artist" loading={true} />

      <MobileRowMeta>
        <MobileRowHeader>
          <TombstoneText style={{ width: isOdd(index) ? 120 : 90 }} />
        </MobileRowHeader>
      </MobileRowMeta>
    </MobileRowContent>
  </MobileRow>
);

LabelSubscribersRowMobile.TEST_IDS = {
  row: 'label-subscribers-row',
  archvieAccess: 'label-subscribers-row--archive-access',
  rowId: (id: string | undefined) => `label-subscriber-row--artistId-${id}`
};

export default LabelSubscribersRowMobile;
